import { gql } from '@apollo/client'
import { graphql } from '@/graphql-setup'

export const PROFILE_QUERY = graphql(`
  query Profile($id: ID!, $withDocuments: Boolean = false) {
    user(id: $id) {
      id
      email
      notificationEmail
      fullName
      profilePicture
      phoneNumber
      address
      city
      postalCode
      birthNumber
      contactPersonName
      contactPersonPhoneNumber
      includeInSalaryCalculation

      internalNumber
      startedAt

      activeDriverCard {
        id
        expiresAt
      }

      driverCards {
        id
        cardIssuingMemberState
        driverIdentification
        issuedAt
        expiresAt
        isExpired
      }

      userTypes {
        id
        name
      }

      salaryGroups {
        id
        name
      }

      salaryWorkTimes {
        id
        priority
        dailyWorkLimit
        periodWorkLimit
        pauseDuration
        pauseThreshold
        paidPauseThreshold
        salaryDateStart
        salaryDateEnd
        name
        useSalaryDayPayment
        salaryType
        useWeekOvertime

        useAutomaticDietGeneration
        useAutomaticSalaryDayGeneration

        defaultSalaryCode {
          id
          name
          externalId
        }

        dayOvertimeSalaryCode {
          id
          name
          externalId
        }

        weekOvertimeSalaryCode {
          id
          name
          externalId
        }

        periodAddons {
          id
          start
          end

          salaryCode {
            id
            name
            externalId
          }

          monday
          tuesday
          wednesday
          thursday
          friday
          saturday
          sunday
        }
        defaultDepartment {
          id
          name
        }
        availableDepartments {
          id
          name
        }
        moduleConfig {
          id
          name
        }
      }

      leaderGroupsAsUser {
        id
        name
      }

      salaryGroups {
        id
        name
      }

      documents @include(if: $withDocuments) {
        edges {
          node {
            id
            name

            expirationDate
            notificationTime
            responsibleUser {
              id
              fullName
            }
            hasFiles
          }
        }
      }
    }
  }
`)

export const USER_CHILDREN_QUERY = graphql(`
  query UserChildren($userId: ID!) {
    userChildren(userId: $userId) {
      id
      name
      dob
      illness
      soleCaregiver
    }
  }
`)

export const USER_SHALLOW_QUERY = graphql(`
  query UserShallow($id: ID!) {
    user(id: $id) {
      id
      fullName
    }
  }
`)

export const USER_FULL_NAME_QUERY = graphql(`
  query UserFullName($id: ID!) {
    user(id: $id) {
      id
      fullName
    }
  }
`)

export const USER_USER_TYPES_QUERY = graphql(`
  query UserUserTypes($id: ID!) {
    user(id: $id) {
      id
      userTypes {
        id
        name
        color
        isAdministrator
      }
    }
  }
`)

export const USER_SALARY_GROUPS_QUERY = graphql(`
  query UserSalaryGroups($id: ID!) {
    user(id: $id) {
      id
      salaryGroups {
        id
        name
      }
    }
  }
`)

export const USER_SALARY_WORK_TIME_QUERY = gql`
  query UserSalaryWorkTime($id: ID!, $moduleConfigName: String) {
    user(id: $id) {
      id
      salaryWorkTime(moduleConfigName: $moduleConfigName) {
        id
        priority
        dailyWorkLimit
        periodWorkLimit
        pauseDuration
        pauseThreshold
        paidPauseThreshold
        salaryDateStart
        salaryDateEnd
        name

        moduleConfig {
          name
        }

        defaultSalaryCode {
          id
          name
          externalId
        }

        periodAddons {
          id
          start
          end

          salaryCode {
            id
            name
            externalId
          }

          monday
          tuesday
          wednesday
          thursday
          friday
          saturday
          sunday
        }
      }
    }
  }
`

export const USERS_QUERY = gql`
  query Users(
    $q: String
    $first: Int
    $after: String
    $orderBy: String
    $userType: String
    $dropdown: String
    $includeActive: Boolean
    $includeInactive: Boolean
    $includeExpiredDocuments: Boolean
    $onlyIncludeUserDocumentCategories: [String]
  ) {
    allUsers(
      first: $first
      after: $after
      q: $q
      orderBy: $orderBy
      userType: $userType
      dropdown: $dropdown
      includeActive: $includeActive
      includeInactive: $includeInactive
      includeExpiredDocuments: $includeExpiredDocuments
      onlyIncludeUserDocumentCategories: $onlyIncludeUserDocumentCategories
    ) {
      totalCount
      missingConfigurationCount
      activeUserCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          firstName
          lastName
          fullName
          email
          notificationEmail
          phoneNumber
          initials
          position
          profilePicture
          isActive
          hasRegistered
          internalNumber
          hasActiveDriverCard
          hasLoginCode
          userTypes {
            id
            name
          }
          driverFetchInfo {
            id
            lastFetch
            integrationEndpoint
          }
        }
      }
    }
  }
`

export const SIMPLE_USERS_QUERY = graphql(`
  query Users(
    $first: Int
    $after: String
    $q: String
    $orderBy: String
    $dropdown: String
    $filterSalaryResponsible: Boolean
    $filterLeaderGroup: Boolean
    $includeInSalaryCalculation: Boolean
    $includeActive: Boolean
    $includeInactive: Boolean
  ) {
    allUsers(
      first: $first
      after: $after
      q: $q
      orderBy: $orderBy
      dropdown: $dropdown
      filterSalaryResponsible: $filterSalaryResponsible
      filterLeaderGroup: $filterLeaderGroup
      includeInSalaryCalculation: $includeInSalaryCalculation
      includeActive: $includeActive
      includeInactive: $includeInactive
    ) {
      edges {
        node {
          id
          firstName
          lastName
          fullName
          email
          internalNumber
          includeInSalaryCalculation
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`)

export const ALL_USERS_SHALLOW_QUERY = gql`
  query UsersShallow(
    $q: String
    $first: Int
    $after: String
    $orderBy: String
    $dropdown: String
    $filterSalaryResponsible: Boolean
    $filterLeaderGroup: Boolean
  ) {
    allUsers(
      q: $q
      first: $first
      after: $after
      orderBy: $orderBy
      dropdown: $dropdown
      filterSalaryResponsible: $filterSalaryResponsible
      filterLeaderGroup: $filterLeaderGroup
    ) {
      edges {
        node {
          id
          firstName
          lastName
          fullName
          email
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

export const USER_TYPES_QUERY = gql`
  query UserTypes($permission: String, $dropdown: String) {
    allUserTypes(permission: $permission, dropdown: $dropdown) {
      edges {
        node {
          id
          name
          users {
            edges {
              node {
                id
                fullName
                username
              }
            }
          }
        }
      }
    }
  }
`

export const USERS_COMBOBOX_QUERY = graphql(`
  query UsersCombobox(
    $dropdown: String
    $includeInactive: Boolean
    $includeSuperuser: Boolean
    $filterSalaryResponsible: Boolean
    $filterLeaderGroup: Boolean
    $includeInSalaryCalculation: Boolean
  ) {
    allUsersList(
      orderBy: "lastName"
      dropdown: $dropdown
      includeInactive: $includeInactive
      includeSuperuser: $includeSuperuser
      filterSalaryResponsible: $filterSalaryResponsible
      filterLeaderGroup: $filterLeaderGroup
      includeInSalaryCalculation: $includeInSalaryCalculation
    ) {
      __typename
      id
      fullName
      email
      internalNumber
      includeInSalaryCalculation
    }
  }
`)

export const USERS_EXTERNAL_IDS_QUERY = graphql(`
  query UsersExternalIds {
    allUsersList(orderBy: "lastName") {
      id
      fullName
      internalNumber
    }
  }
`)

export const USER_TYPES_SHALLOW_QUERY = gql`
  query UserTypesShallow {
    allUserTypes {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`

export const USER_TYPES_WITH_PERMISSIONS_QUERY = gql`
  query UserTypesWithPermissions {
    allUserTypes {
      edges {
        node {
          id
          name
          editable
          isAdministrator
          permissions
        }
      }
    }
  }
`

export const USER_TYPES_WITH_DROPDOWNS_QUERY = gql`
  query UserTypesWithDropdowns {
    allUserTypes {
      edges {
        node {
          id
          name
          dropdowns
        }
      }
    }
  }
`

export const DRIVER_CARD_QUERY = gql`
  query DriverCard($id: ID!) {
    driverCard(id: $id) {
      id
      name
      cardIssuingMemberState
      driverIdentification
      issuedAt
      expiresAt
      isExpired
      user {
        id
        fullName
      }
    }
  }
`

export const USER_DOCUMENT_FILES_FOR_DOCUMENT_QUERY = gql`
  query UserDocumentFilesForDocument($userDocument: ID) {
    allUserDocumentFiles(userDocument: $userDocument) {
      edges {
        node {
          id
          file
          name
          originalName
          createdAt
        }
      }
    }
  }
`

export const ALL_USER_DOCUMENTS_QUERY = gql`
  query AllUserDocuments(
    $q: String
    $user: ID
    $responsibleUser: ID
    $users: [ID]
    $responsibleUsers: [ID]
    $expirationStatus: String
    $expirationDateGte: DateTime
    $expirationDateLte: DateTime
    $orderBy: String
    $first: Int
    $after: String
    $categories: [ID]
  ) {
    allUserDocuments(
      q: $q
      user: $user
      responsibleUser: $responsibleUser
      users: $users
      responsibleUsers: $responsibleUsers
      expirationStatus: $expirationStatus
      expirationDate_Gte: $expirationDateGte
      expirationDate_Lte: $expirationDateLte
      orderBy: $orderBy
      first: $first
      after: $after
      categories: $categories
    ) {
      edges {
        node {
          id
          name

          expirationDate
          notificationTime

          hasFiles

          user {
            id
            fullName
          }

          responsibleUser {
            id
            fullName
            firstName
            lastName
          }
          categories {
            id
            name
          }
        }
      }
    }
  }
`

export const OWN_DOCUMENT_NOTIFICATION_STATUS_QUERY = gql`
  query OwnDocumentNotificationStatus($id: ID!) {
    user(id: $id) {
      id
      fullName
      ownDocumentNotificationStatus
    }
  }
`

export const NEEDS_PASSWORD_CHANGE_QUERY = gql`
  query NeedsPasswordChange {
    me {
      id
      needsPasswordChange
    }
  }
`

export const ALL_USER_DOCUMENT_CATEGORIES_QUERY = gql`
  query AllUserDocumentCategories($includeExpiredDocuments: Boolean = false) {
    allUserDocumentCategories {
      edges {
        node {
          id
          name
          editable
          numUsers(includeExpiredDocuments: $includeExpiredDocuments)
        }
      }
    }
  }
`

export const ALL_USER_EQUIPMENT_TYPES_QUERY = gql`
  query AllUserEquipmentTypes {
    allUserEquipmentTypes {
      edges {
        node {
          id
          name
          description
          editable
        }
      }
    }
  }
`

export const ALL_USER_EQUIPMENT_QUERY = graphql(`
  query AllUserEquipment($user: ID, $equipmentType: ID, $orderBy: String) {
    allUserEquipment(
      user: $user
      equipmentType: $equipmentType
      orderBy: $orderBy
    ) {
      edges {
        node {
          id
          name
          assignedDate
          returnDate
          user {
            id
            fullName
          }
          equipmentType {
            id
            name
          }
        }
      }
    }
  }
`)
